import * as React from 'react';
import { useCallback, useEffect, useMemo, useRef } from 'react';

import isEqual from 'lodash/isEqual';

import initBem from 'js/lib/bem';

import GradeNotification from 'bundles/compound-assessments/components/form-parts/GradeNotification';
import type { FormPartsValidationStatus } from 'bundles/compound-assessments/components/form-parts/lib/checkResponsesInvalid';
import { typeNames } from 'bundles/compound-assessments/constants';
import type { WidgetPrompt, WidgetPromptResponseDefinition } from 'bundles/compound-assessments/types/FormParts';
import WidgetFrame from 'bundles/widget/components/cds/WidgetFrame';
import { RPCActions } from 'bundles/widget/constants/WidgetConstants';
import { useWidgetSessionQuery } from 'bundles/widget/hooks/useWidgetSessionQuery';
import type { CourseraConnectMessage } from 'bundles/widget/types/Request';

const bem = initBem('Widget');

type WidgetResponse = {
  typeName: typeof typeNames.AUTO_GRADABLE_RESPONSE;
  definition: {
    value: {
      answer: any;
    };
  };
};

export type Props = {
  prompt?: WidgetPrompt;
  onChangeResponse: (response: WidgetResponse) => void;
  isDisabled: boolean;
};

export const checkInvalid = (response: any): FormPartsValidationStatus | null => null;

const WidgetQuestion = ({ onChangeResponse, prompt, isDisabled }: Props) => {
  const widgetSessionId = prompt?.variant?.definition?.widgetSessionId;
  const widgetSessionIdFromPrompt = (prompt?.feedback?.definition as WidgetPromptResponseDefinition)?.widgetSessionId;

  const { widgetSession, fetchWidgetSession } = useWidgetSessionQuery({
    sessionId: widgetSessionIdFromPrompt ?? widgetSessionId,
  });

  const previousPrompt = useRef<WidgetPrompt>();

  useEffect(() => {
    const prevPrompt = previousPrompt?.current;
    if (prompt && prevPrompt) {
      let sessionId = prompt.variant?.definition?.widgetSessionId;
      if (prompt.feedback) {
        sessionId = (prompt.feedback.definition as WidgetPromptResponseDefinition)?.widgetSessionId;
      }
      let prevSessionId = prevPrompt.variant?.definition?.widgetSessionId;
      if (prevPrompt.feedback) {
        prevSessionId = (prevPrompt.feedback.definition as WidgetPromptResponseDefinition)?.widgetSessionId;
      }
      if (sessionId !== prevSessionId) {
        fetchWidgetSession({ sessionId });
      }
    }

    if (!isEqual(prompt, prevPrompt)) {
      previousPrompt.current = prompt;
    }
  }, [prompt, fetchWidgetSession]);

  const onReceiveWidgetMessage = useCallback<(...args: $TSFixMe[]) => $TSFixMe>(
    (request: CourseraConnectMessage) => {
      if (request.type === RPCActions.SET_ANSWER) {
        onChangeResponse({
          typeName: typeNames.AUTO_GRADABLE_RESPONSE,
          definition: {
            value: {
              answer: request.body?.answer,
            },
          },
        });
      }
      return Promise.resolve();
    },
    [onChangeResponse]
  );

  return (
    <div>
      {widgetSession && (
        <WidgetFrame
          /* Note that when the assessment is graded and there is feedback widgetSessionID as well as the original widgetSessionID associated with this question, the widgetSession here continues to be the assessment widget session ID.  This is honestly weird, but it's been left as is,  since the widgetSession ID is used to make the RPC Action calls. Updating this to use the graded feedback widgetSession ID may break rpc action calls in existing widgets so this has been left as is. */
          sessionId={widgetSessionId}
          /* This session is either the wigetSession associated with the question, or the session associated with the graded feedback. */
          // @ts-expect-error DateTime: string
          session={widgetSession}
          widgetId={`widget-question-${prompt?.id}`}
          parentRpcActions={[RPCActions.SET_ANSWER]}
          onReceiveMessage={onReceiveWidgetMessage}
          isDisabled={isDisabled}
        />
      )}
    </div>
  );
};

export const Widget = (props: Props) => {
  const { prompt, onChangeResponse, isDisabled } = props;

  const isPartialFeedback = useMemo(() => prompt?.variant?.detailLevel === 'Partial', [prompt?.variant?.detailLevel]);

  if (!prompt) {
    return null;
  }

  return (
    <div className={bem()}>
      {!isPartialFeedback && (
        <div className={bem('response')}>
          <WidgetQuestion prompt={prompt} onChangeResponse={onChangeResponse} isDisabled={isDisabled} />
        </div>
      )}
      <GradeNotification prompt={prompt} />
    </div>
  );
};
export default Widget;
